import type { Point } from "../canvas/canvasTypes";
import type { Rect } from "../comic/comicTypes";
import type { ShapeGeometry } from "./shapeTypes";

export const SHAPE_ACTIVATION_THRESHOLD_CSS_PIXELS = 4;
export const MIN_SHAPE_DIMENSION_PROJECT_PIXELS = 2;

export function clampShapePoint(point: Readonly<Point>, clip: Readonly<Rect>): Point {
  const right = clip.x + clip.width;
  const bottom = clip.y + clip.height;
  return {
    x: Math.round(Math.min(right, Math.max(clip.x, Number.isFinite(point.x) ? point.x : clip.x))),
    y: Math.round(Math.min(bottom, Math.max(clip.y, Number.isFinite(point.y) ? point.y : clip.y))),
  };
}

export function normalizeShapeGeometry(
  start: Readonly<Point>,
  end: Readonly<Point>,
  clip: Readonly<Rect>,
): ShapeGeometry {
  return Object.freeze({
    start: Object.freeze(clampShapePoint(start, clip)),
    end: Object.freeze(clampShapePoint(end, clip)),
  });
}

export function normalizedShapeRect(geometry: Readonly<ShapeGeometry>): Rect {
  const left = Math.min(geometry.start.x, geometry.end.x);
  const top = Math.min(geometry.start.y, geometry.end.y);
  return {
    x: left,
    y: top,
    width: Math.abs(geometry.end.x - geometry.start.x),
    height: Math.abs(geometry.end.y - geometry.start.y),
  };
}

export function isUsableShapeGeometry(
  tool: "line" | "rectangle" | "ellipse",
  geometry: Readonly<ShapeGeometry>,
): boolean {
  if (tool === "line") {
    return (
      Math.hypot(geometry.end.x - geometry.start.x, geometry.end.y - geometry.start.y) >=
      MIN_SHAPE_DIMENSION_PROJECT_PIXELS
    );
  }
  const rect = normalizedShapeRect(geometry);
  return (
    rect.width >= MIN_SHAPE_DIMENSION_PROJECT_PIXELS &&
    rect.height >= MIN_SHAPE_DIMENSION_PROJECT_PIXELS
  );
}

export function shapeTravelCssPixels(
  start: Readonly<Point>,
  current: Readonly<Point>,
  zoom: number,
): number {
  return Math.hypot(current.x - start.x, current.y - start.y) * Math.max(0.05, zoom);
}
